import { unionBounds } from '@/utils/bounds-kit';
import { createSVGElement } from '@/utils/svg-kit';
import type { Bounds } from '@/version-types';
import type { ReadyTextRenderArtText } from '../../assets';
import type { TextRenderArtText, TextRenderInput } from '../../types';
import type { RenderContext } from '../context';
import { buildArtTextFragments } from './fragments';
import { normalizeSVGId, parseArtTextMaterial } from './material';

export type ArtTextVisual = {
    materialBounds: Bounds | null;
    paintBounds: Bounds | null;
    transforms: Map<number, string>;
    draw: (target: SVGElement) => void;
};

/**
 * 读取当前输入已 ready 的花字请求。
 *
 * @param input - 当前 Text Render 输入。
 * @param getArtText - 只读取花字素材的具名 getter。
 * @returns 请求和 ready SVG；未声明或未 ready 时返回 null。
 */
const resolveArtText = (
    input: TextRenderInput,
    getArtText?: (request: TextRenderArtText) => ReadyTextRenderArtText | undefined,
): { request: TextRenderArtText; asset: ReadyTextRenderArtText } | null => {
    const request = input.artText;
    if (!request || !getArtText) return null;
    const asset = getArtText(request);
    return asset ? { request, asset } : null;
};

/**
 * 构建 Advanced Art Text 素材、字符旋转和视觉边界状态。
 *
 * @param context - 当前一轮不变的输入、snapshot 与命名空间。
 * @param getArtText - 只读取花字素材的具名 getter。
 * @returns 结构完整且素材 ready 时的花字状态，否则返回 undefined。
 */
export const resolveArtTextVisual = (
    context: RenderContext,
    getArtText?: (request: TextRenderArtText) => ReadyTextRenderArtText | undefined,
): ArtTextVisual | undefined => {
    const { input, snapshot, namespace } = context;
    const resolved = resolveArtText(input, getArtText);
    if (!resolved || input.writingMode !== 'horizontal-tb') return undefined;
    const fragments = buildArtTextFragments(input, snapshot);
    const parsed = parseArtTextMaterial(resolved.asset.svgText);
    const defaultFontSize = resolved.request.defaultFontSize;
    if (!parsed || fragments.length === 0 || !defaultFontSize || defaultFontSize <= 0) return undefined;
    const useCount = resolved.request.useCount;
    if (useCount && parsed.shapes.length !== useCount) return undefined;
    const ratio = input.fontSize / defaultFontSize;
    if (!Number.isFinite(ratio) || ratio <= 0) return undefined;

    const [sourceX, sourceY, sourceWidth, sourceHeight] = parsed.viewBox;
    const unitWidth = sourceWidth / (useCount || 1);
    const renderedWidth = unitWidth * ratio;
    const renderedHeight = sourceHeight * ratio;
    const materialNode = createSVGElement('g', { 'data-art-text-material': '' });
    const defs = createSVGElement('defs');
    const idPrefix = `art-text-${normalizeSVGId(namespace)}-${normalizeSVGId(resolved.request.id)}`;
    parsed.root.querySelectorAll(':scope > defs').forEach((sourceDefs) => {
        Array.from(sourceDefs.childNodes).forEach((node) => defs.appendChild(node.cloneNode(true)));
    });
    parsed.shapes.forEach((shape, index) => {
        const clone = shape.cloneNode(true) as SVGElement;
        clone.setAttribute('id', `${idPrefix}-${index}`);
        defs.appendChild(clone);
    });
    materialNode.appendChild(defs);

    let materialBounds: Bounds | null = null;
    let paintBounds: Bounds | null = null;
    const transforms = new Map<number, string>();
    fragments.forEach((fragment) => {
        const slot = useCount ? fragment.visibleIndex % useCount : 0;
        const x = fragment.left + (fragment.width - renderedWidth) / 2;
        const y = fragment.top + (fragment.height - renderedHeight) / 2;
        const use = createSVGElement('use', {
            href: `#${idPrefix}-${slot}`,
            transform: `translate(${x} ${y}) scale(${ratio}) translate(${-sourceX - slot * unitWidth} ${-sourceY})`,
        });
        materialNode.appendChild(use);
        materialBounds = unionBounds(materialBounds, {
            left: x,
            top: y,
            right: x + renderedWidth,
            bottom: y + renderedHeight,
        });
        paintBounds = unionBounds(paintBounds, fragment.paintBounds);
        if (fragment.rotate) {
            const transform = `rotate(${fragment.rotate} ${fragment.centerX} ${fragment.centerY})`;
            transforms.set(fragment.spanIndex, transform);
        }
    });

    return {
        materialBounds,
        paintBounds,
        transforms,
        draw: (target) => target.appendChild(materialNode),
    };
};
